import { ZfBox } from '@zafire/react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import moment from 'moment/moment';
import type { FunctionComponent } from 'react';
import { useMemo, useRef } from 'react';
import { useCashFlow, useClient, useEvents, useGeneralProjectionSettings, useProjectionCashEventSettings } from '../../../api';
import { DEFAULT_TIME_RANGE } from '../../../config/constants';
import { useCashFlowSeriesForClientOutput } from '../../../hooks/chart/useCashFlowSeriesForClientOutput';
import { useChartBaseCustomizationCallbacks } from '../../../hooks/chart/useChartBaseCustomizationCallbacks';
import { useChartFormatter } from '../../../hooks/chart/useChartFormatter';
import { useYearEventMap } from '../../../hooks/chart/useYearEventMap';
import type { ExtendedSeriesOptionType } from '../../../interfaces/projection';
import { getProjectionYears } from '../../../utils/utils';
import { CashEventPane } from '../../projection/cashEventPane/CashEventPane';
import { getCashflowOptions } from './cashflowChartOptions';

const CASHFLOW_CHART_TYPE = 'CASH_FLOW';

interface IProps {
    isSecondPage: boolean;
}

export const ClientPackageCashflow: FunctionComponent<IProps> = ({ isSecondPage }) => {
    const xAxisYPosition = useRef<number>(undefined);

    const { data: cashFlow } = useCashFlow();
    const { data: client } = useClient();
    const { data: generalSettings } = useGeneralProjectionSettings();
    const { data: cashEventSettings } = useProjectionCashEventSettings();
    const {
        data: { lifeEvents, maturities },
    } = useEvents();

    const { seriesData, globalMin, globalMax } = useCashFlowSeriesForClientOutput(cashFlow ?? []);

    const customizeChartBase = useChartBaseCustomizationCallbacks({ clientBirthdate: client?.birthdate ?? '', isClientOutput: true });

    const cashFlowRelevantEvents = () => {
        if (cashEventSettings?.enabled) {
            return [...maturities, ...lifeEvents].filter((event) => {
                return cashEventSettings.itemsSettings.some((itemSetting) => itemSetting.configurableKey === event.configurableKey && itemSetting.enabled);
            });
        }

        return [];
    };

    const { xAxisLabelsFormatter } = useChartFormatter({
        chartType: CASHFLOW_CHART_TYPE,
        clientBirthdate: client?.birthdate ?? '',
        series: seriesData as ExtendedSeriesOptionType[],
        isClientOutput: true,
    });

    const projectionYears = useMemo(() => getProjectionYears(generalSettings?.projectionPeriodYears ?? 0), [generalSettings]);

    const { yearEventMap, updateEventCoordinates } = useYearEventMap({
        xAxisYPosition,
        allEvents: cashFlowRelevantEvents(),
        projectionPeriodYears: projectionYears,
    });

    const highchartsReact = useMemo(
        () => (
            <HighchartsReact
                containerProps={{ className: 'chart-container__chart chart-container__client-package' }}
                id={'container'}
                highcharts={Highcharts}
                constructorType="chart"
                options={getCashflowOptions({
                    clientBirthdate: client?.birthdate ?? '',
                    onChartLoadEventHandlers: [],
                    onChartRenderEventHandlers: [updateEventCoordinates, customizeChartBase],
                    // @ts-expect-error Problems with missing Highcharts types
                    refs: { xAxisYPosition },
                    seriesData,
                    xAxisLabelsFormatterFn: xAxisLabelsFormatter,
                    legendFontSize: '2rem',
                    xAxisFontSize: '2rem',
                    yearRange: {
                        from: isSecondPage ? moment().year() + DEFAULT_TIME_RANGE : moment().year(),
                        to: isSecondPage ? moment().year() + DEFAULT_TIME_RANGE * 2 - 1 : moment().year() + DEFAULT_TIME_RANGE - 1,
                    },
                    yAxisMin: globalMin,
                    yAxisMax: globalMax,
                })}
            />
        ),
        [seriesData, client?.birthdate, xAxisLabelsFormatter, customizeChartBase, updateEventCoordinates, isSecondPage, globalMin, globalMax],
    );

    return (
        <ZfBox style={{ position: 'relative', display: 'flex', width: '100%', height: '100%' }}>
            <ZfBox style={{ flexGrow: 1 }}>{highchartsReact}</ZfBox>
            {yearEventMap && <CashEventPane clientOutput={true} yearEventMap={yearEventMap} />}
        </ZfBox>
    );
};
